import { Injectable } from '@angular/core';
import moment from 'moment-timezone';
import { BehaviorSubject } from 'rxjs';
import * as CryptoJS from 'crypto-js';

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  
  readonly DATE_FORMAT: string = 'D/MM/YYYY';
  readonly ENCRYPTION_KEY = '4bX9z3vQN2WcE7YpJgHdLrMfTuXsaK1!';
  readonly IV_LENGTH = 16;

  public userCredentials: any = null;
  private userStore: { [key: string]: any } = {};

  private selectedSiteSource = new BehaviorSubject<any | null>(null);
  selectedSite$ = this.selectedSiteSource.asObservable();

  private lastSelectedDeviceId: string | null = null;

  private userPermissions = new BehaviorSubject<any>([]);
  userPermissions$ = this.userPermissions.asObservable();

  readonly UI_MESSAGES = {
    LOADING: 'Data is loading...',
    EMPTY: 'No data available',
  };

  readonly PORTAL_CONFIG: string = 'portalConfig';
  private readonly STORAGE_KEYS: string[] = [
    'username',
    'email',
    'permissionlevel',
    'timeZone',
    'sortMode',
    'roleRank',
    'roleId',
    'portalName',
    'userId',
  ];

  constructor() {
    this.STORAGE_KEYS.forEach((key) => {
      const value = sessionStorage.getItem(key);
      if (!value) return;
      if (value) {
        this.userStore[key] = value;
      }
    });
  }

  getPortalConfiguration() {
    if (!this.userCredentials) {
      const storedCredentials = sessionStorage.getItem(this.PORTAL_CONFIG);
      if (storedCredentials)
        this.userCredentials = JSON.parse(storedCredentials);
    }
    return this.userCredentials;
  }

  getUserPermissions(): any[] {
    const creds = this.getPortalConfiguration();
    const permissions = creds?.permissions ?? [];
    return permissions;
  }

  hasPermission(slug: string): boolean {
    const match = this.findModuleBySlug(slug, this.getUserPermissions());
    return !!match;
  }

  private findModuleBySlug(slug: string, modules: any[]): any | null {
    for (const item of modules || []) {
      if (item?.moduleSlug === slug) {
        return item;
      }
      if (Array.isArray(item?.subModules)) {
        const found = this.findModuleBySlug(slug, item.subModules);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  findFirstAccessibleSlug(): string | null {
    const modules = this.getUserPermissions();

    const walk = (items: any[]): any | null => {
      for (const item of items || []) {
        if (item?.enabled === true && item?.moduleSlug && item?.defaultTab !== true) {
          return item;
        }
        if (Array.isArray(item?.subModules) && item.subModules.length > 0) {
          const found = walk(item.subModules);
          if (found) return found;
        }
      }
      return null;
    };

    return walk(modules)?.moduleSlug ?? null;
  }

  findDefaultTabSlug(): string | null {
    const modules = this.getUserPermissions();

    const walk = (items: any[]): any | null => {
      for (const item of items || []) {
        if (item?.defaultTab === true) {
          return item;
        }
        if (Array.isArray(item?.subModules) && item.subModules.length > 0) {
          const found = walk(item.subModules);
          if (found) return found;
        }
      }
      return null;
    };

    return walk(modules)?.moduleSlug ?? null;
  }

  public setUserProperty(key: string, value: any): void {
    this.userStore[key] = value;
    sessionStorage.setItem(key, value);
  }

  setPortalConfiguration(credentials: any) {
    this.userCredentials = credentials;
    sessionStorage.setItem(this.PORTAL_CONFIG, JSON.stringify(credentials));
  }

  setModuleList(module: any[]): void {
    this.userPermissions.next(module);
  }

  get(key: string): any {
    if (this.userStore[key] !== undefined) {
      return this.userStore[key];
    }

    const stored = sessionStorage.getItem(key);
    if (stored !== null) {
      this.userStore[key] = stored;
      return stored;
    }

    return null;
  }

  getInterval(): any[] {
    const intervalData = this.getPortalConfiguration();
    return (intervalData?.interval || []).sort(
      (a: any, b: any) => a.rank - b.rank,
    );
  }

  getFold(): any[] {
    const foldData = this.getPortalConfiguration();
    return (foldData?.fold || []).sort((a: any, b: any) => a.rank - b.rank);
  }

  setSelectedSite(site: any) {
    this.selectedSiteSource.next(site);
  }

  setLastSelectedDeviceId(id: string | null): void {
    this.lastSelectedDeviceId = id === null || id === undefined ? null : String(id);
  }

  getLastSelectedDeviceId(): string | null {
    return this.lastSelectedDeviceId;
  }

  convertMomentToHighchartsFormat(momentFormat: string): string {
    const formatMap: { [key: string]: string } = {
      YYYY: '%Y',
      MM: '%m',
      D: '%d',
      hh: '%I', // 12-hour format
      HH: '%H', // 24-hour format
      mm: '%M',
      ss: '%S',
      A: '%p', // AM/PM uppercase
      a: '%P', // AM/PM lowercase
    };
    let highchartsFormat = momentFormat;
    for (const [momentKey, highchartsKey] of Object.entries(formatMap)) {
      highchartsFormat = highchartsFormat.replace(momentKey, highchartsKey);
    }
    return highchartsFormat;
  }

  convertUTCtoTimeZone(ts: string, timezone?: any | null): string {
    if (!timezone) {
      timezone = this.get('timeZone');
    }
    const formattedTimestamp = moment
      .utc(ts)
      .tz(timezone)
      .format('YYYY-MM-DDTHH:mm:ss.SSS');
    return formattedTimestamp ? formattedTimestamp : ts;
  }

  formatSensorReadingValue(value: any, sensorDict: any): string {
    if (!sensorDict) return '';
    const { unit, decimalPlaces = 2, valueMap } = sensorDict;

    if ( value === undefined || value === '') {
      return `${(0).toFixed(decimalPlaces)}${unit && unit !== 'boolean' ? ` ${unit}` : ''}`;
    }
    if (sensorDict.kind === 'Bool') {
      if (!valueMap) {
        return String(value);
      }
      return String(valueMap[value]);
    }
    const numericValue = Number(value);
    if (!isNaN(numericValue)) {
      const commaSeparatedValue = this.formatNumericValue(
        numericValue,
        decimalPlaces,
      );
      return `${commaSeparatedValue}${unit && unit !== 'boolean' ? ` ${unit}` : ''}`;
    }
    return String(value);
  }

  encryption(text: string): string {
    const iv = CryptoJS.lib.WordArray.random(this.IV_LENGTH);
    const encrypted = CryptoJS.AES.encrypt(
      text,
      CryptoJS.enc.Utf8.parse(this.ENCRYPTION_KEY),
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      },
    );

    return (
      iv.toString(CryptoJS.enc.Hex) +
      ':' +
      encrypted.ciphertext.toString(CryptoJS.enc.Hex)
    );
  }

  getCurrentModuleSlug(url: any): string {
    const cleanUrl = url.split('?')[0].split('#')[0];
    const segments = cleanUrl.split('/').filter(Boolean);
    return segments.length > 1 ? segments[1] : segments[0] || '';
  }

  getModuleBySlug(slug: string): any {
    const permissions = this.getUserPermissions?.() || [];
    const topLevelModule = permissions.find((m) => m.moduleSlug === slug);
    if (topLevelModule) {
      return topLevelModule;
    }

    for (const module of permissions) {
      const subModule = module?.subModules?.find((sub: any) => sub?.moduleSlug === slug);
      if (subModule) {
        return subModule;
      }
    }

    return null;
  }

  getCurrentModule(url: any): any {
    const moduleSlug = this.getCurrentModuleSlug(url);
    const findModule = this.getModuleBySlug(moduleSlug);
    if (findModule) {
      return {
        moduleId: findModule?.id ?? findModule?._id,
        selectedNode: findModule.defaultTreeNodeId,
      };
    }
    return { moduleId: '', selectedNode: '' };
  }

  formattedAxisValue(value: any, sensorDict: any): string {
    if (!sensorDict) return '';
    const { unit, decimalPlaces = 2, valueMap, id_name } = sensorDict;

    if (value === null || value === undefined || value === '') {
      return `${(0).toFixed(decimalPlaces)}${unit && unit !== 'boolean' ? ` ${unit}` : ''}`;
    }
    if (sensorDict.kind === 'Bool') {
      if (!valueMap) {
        return String(value);
      }
      return String(valueMap[value]);
    }
    if (!isNaN(Number(value))) {
      const commaSeparatedValue = this.formatXAxisLabel(Number(value));
      return `${commaSeparatedValue}${unit && unit !== 'boolean' ? ` ${unit}` : ''}`;
    }
    return `${value}${unit ? ` ${unit}` : ''}`;
  }

  formatXAxisLabel(value: number): string {
    if (value === null || value === undefined || isNaN(Number(value))) {
      return String(value);
    }

    const num = Number(value);

    if (Math.abs(num) >= 1_000_000) {
      return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    }

    if (Math.abs(num) >= 1_000) {
      return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
    }

    return String(num);
  }

  private formatNumericValue(value: number, decimals: number): string {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
      useGrouping: true,
    }).format(value);
  }

  adjustAvailableIntervalsBasedOnDateRange(range: { start: any; end: any }) {
    // Parse with explicit format so moment uses local time, not UTC midnight
    const startDate = moment(range.start, 'YYYY-MM-DD', true).isValid()
      ? moment(range.start, 'YYYY-MM-DD')
      : moment(range.start);
    const endDate = moment(range.end, 'YYYY-MM-DD', true).isValid()
      ? moment(range.end, 'YYYY-MM-DD')
      : moment(range.end);

    if (!startDate.isValid() || !endDate.isValid()) {
      console.error('Invalid date range', range);
      return [];
    }

    const days = Math.ceil(endDate.diff(startDate, 'days', true));
    const isOneYearOrMore =
      endDate.diff(startDate, 'years', true) >= 1 ||
      endDate.diff(startDate, 'days') >= 364;

    const allIntervals = this.getInterval();
    let selectedIntervals = [];

    if (days <= 1) {
      selectedIntervals = allIntervals.filter(
        (i) => i.val === 'minutes' || i.val === 'hours',
      );
    } else if (days <= 14) {
      selectedIntervals = allIntervals.filter((i) =>
        ['minutes', 'hours', 'day'].includes(i.val),
      );
    } else if (!isOneYearOrMore) {
      selectedIntervals = allIntervals.filter(
        (i) => i.val !== 'minutes' && i.val !== 'year',
      );
    } else {
      selectedIntervals = allIntervals.filter((i) =>
        ['day', 'week', 'year'].includes(i.val),
      );
    }
    return selectedIntervals.sort((a, b) => a.rank - b.rank);
  }

  adjustInterval(start: any, end: any, intervalFilter: any[]) {
    // Parse with explicit format so moment uses local time, not UTC midnight
    const startMoment = moment(start, 'YYYY-MM-DD', true).isValid()
      ? moment(start, 'YYYY-MM-DD')
      : moment(start);
    const endMoment = moment(end, 'YYYY-MM-DD', true).isValid()
      ? moment(end, 'YYYY-MM-DD')
      : moment(end);

    if (!startMoment.isValid() || !endMoment.isValid()) {
      console.error('Invalid date format');
      return null;
    }

    // Use days diff to avoid DST ambiguity on boundary days, then convert to hours
    const hoursDiff = endMoment.diff(startMoment, 'days') * 24;

    let expected: any;

    if (hoursDiff <= 24) {
      expected = { value: 1, val: 'minutes' };
    }else if(hoursDiff <= 72) {
       expected = { value: 5, val: 'minutes' };
    }
     else if (hoursDiff <= 168) {
      expected = { value: 15, val: 'minutes' };
    } else if (hoursDiff <= 720) {
      expected = { value: 30, val: 'minutes' };
    } else if (hoursDiff <= 2160) {
      expected = { value: 1, val: 'hours' };
    } else {
      expected = { value: 1, val: 'day' };
    }

    const order = ['minutes', 'hours', 'day', 'week', 'month', 'year'];

    const sortedIntervals = [...intervalFilter].sort((a, b) => {
      const diff = order.indexOf(a.val) - order.indexOf(b.val);
      return diff === 0 ? a.value - b.value : diff;
    });

    let selectedItem = sortedIntervals.find(
      (i) => i.val === expected.val && i.value === expected.value,
    );

    if (!selectedItem) {
      selectedItem = sortedIntervals.find(
        (i) => i.val === expected.val && i.value > expected.value,
      );
    }

    if (!selectedItem) {
      const expectedIndex = order.indexOf(expected.val);
      selectedItem = sortedIntervals.find(
        (i) => order.indexOf(i.val) > expectedIndex,
      );
    }

    if (!selectedItem) {
      selectedItem = sortedIntervals[0] || null;
    }

    return selectedItem;
  }

  sortTree(data: any[]): any[] {
    const config = this.getSafeSortConfig();
    const comparator = this.getComparator(config);

    // Sort nodes at this level
    const sorted = data.sort((a, b) => {
      if (config?.sortType === 'alphabetical') {
        return comparator(a, b, 'text');
      }
      return comparator(a, b, 'rank');
    });

    // Recursively process children
    return sorted.map((node) => {
      if (node.childItems?.length) {
        node.childItems = this.sortTree(node.childItems);
      }
      if (node.sensors?.length) {
        node.sensors = node.sensors.sort((a: any, b: any) => {
          if (config?.sortType === 'alphabetical') {
            return comparator(a, b, 'id_name');
          }
          return comparator(a, b, 'rank');
        });
      }
      return node;
    });
  }

  getSafeSortConfig(): any {
    const config = this.getPortalConfiguration()?.sortMode
    return {
      sortType: config?.sortType ?? 'manual',
      sortOrder: config?.sortOrder ?? 'asc'
    };
  }

  private getComparator(config: any) {
    const isAsc = config?.sortOrder !== 'desc';

    return (a: any, b: any, key: string) => {
      const rawA = a[key] ?? '';
      const rawB = b[key] ?? '';

      if (typeof rawA === 'string' || typeof rawB === 'string') {
        const valA = String(rawA).toLowerCase();
        const valB = String(rawB).toLowerCase();
        return isAsc ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }
      return isAsc ? (rawA as number) - (rawB as number) : (rawB as number) - (rawA as number);
    };
  }

  sortList(list: any[]): any[] {
    const config = this.getSafeSortConfig();
    return list.sort((a: any, b: any) => {

      if (config.sortType === 'alphabetical') {
        const valA = (a.text ?? '').toLowerCase();
        const valB = (b.text ?? '').toLowerCase();

        return config.sortOrder === 'desc' ? valB.localeCompare(valA) : valA.localeCompare(valB);
      }

      const grankDiff = (a.grank ?? 0) - (b.grank ?? 0);

      if (grankDiff !== 0) {
        return config.sortOrder === 'desc' ? -grankDiff : grankDiff;
      }

      const rankDiff = (a.rank ?? 0) - (b.rank ?? 0);

      return config.sortOrder === 'desc' ? -rankDiff : rankDiff;
    });
  }

  getBaseRoute(url: any): string {
    const path = url.split('?')[0];       // remove query params
    const segments = path.split('/');
    return segments[1] || '';
  }
}
